import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { XIcon } from '@phosphor-icons/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import { DateFieldInput } from '../EmployeeForm/DateFieldInput'

export interface TeamFormData {
  name: string
  description: string
  startDate: Date
  endDate: Date | null
}

interface TeamModalInitialValues {
  name: string
  description: string
  startDate: Date | string
  endDate?: Date | string | null
}

interface TeamModalProps {
  show: boolean
  mode: 'add' | 'edit'
  tribeName: string
  initialValues?: TeamModalInitialValues
  onClose: () => void
  onSubmit: (data: TeamFormData) => Promise<boolean | void> | boolean | void
}

const parseDate = (dateLike?: Date | string | null): Date | null => {
  if (!dateLike) return null
  const mDate = moment(dateLike)
  if (!mDate.isValid()) return null
  return mDate.startOf('day').toDate()
}

export const TeamModal = ({
  show,
  mode,
  tribeName,
  initialValues,
  onClose,
  onSubmit,
}: TeamModalProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = mode === 'edit'

  useEffect(() => {
    if (!show) {
      setIsSubmitting(false)
      return
    }

    setName(initialValues?.name ?? '')
    setDescription(initialValues?.description ?? '')
    setStartDate(parseDate(initialValues?.startDate))
    setEndDate(parseDate(initialValues?.endDate))
  }, [show, mode])

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const canSave = name.trim().length > 0 && startDate !== null

  const handleSubmit = async () => {
    if (!canSave || !startDate || isSubmitting) return
    setIsSubmitting(true)
    try {
      const result = await onSubmit({
        name: name.trim(),
        description: description.trim(),
        startDate,
        endDate,
      })
      if (result === false) return
    } finally {
      setIsSubmitting(false)
    }
  }

  const title = isEditMode
    ? `Edit ${initialValues?.name ?? ''} Team`
    : 'Add Team'

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="team-modal-dialog"
      contentClassName="team-modal-content"
    >
      <div className="team-modal">
        <div className="team-modal__header">
          <div>
            <h2 className="team-modal__title">{title}</h2>
            <p className="team-modal__subtitle">Tribe: {tribeName}</p>
          </div>
          <button
            className="team-modal__close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <XIcon size={20} weight="regular" />
          </button>
        </div>

        <div className="team-modal__content">
          <div className="team-modal__fields">
            <div className="team-modal__field">
              <label className="team-modal__label team-modal__label--required">
                Team Name
              </label>
              <input
                type="text"
                className="team-modal__input"
                placeholder="e.g. ATLAS-001"
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={25}
              />
            </div>

            <div className="team-modal__field">
              <label className="team-modal__label">Description</label>
              <textarea
                className="team-modal__textarea"
                placeholder="Short description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={50}
                rows={2}
              />
            </div>

            <div className="team-modal__dates">
              <div className="team-modal__field">
                <label className="team-modal__label team-modal__label--required">
                  Start Date
                </label>
                <DatePicker
                  selected={startDate}
                  maxDate={endDate ?? undefined}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  onChange={date => setStartDate(date)}
                  customInput={<DateFieldInput />}
                />
              </div>
              <div className="team-modal__field">
                <label className="team-modal__label">End Date</label>
                <DatePicker
                  selected={endDate}
                  minDate={startDate ?? undefined}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  onChange={date => setEndDate(date)}
                  customInput={
                    <DateFieldInput
                      clearable
                      onClear={() => setEndDate(null)}
                    />
                  }
                />
              </div>
            </div>
          </div>

          <div className="team-modal__footer">
            <button
              type="button"
              className="team-modal__btn team-modal__btn--cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="team-modal__btn team-modal__btn--save"
              onClick={handleSubmit}
              disabled={!canSave || isSubmitting}
            >
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default TeamModal
